import axios from 'axios'

import { PER_PAGE_OPTIONS } from '@/constants/pagination'
import { getISOWeekNumber, getWeekBounds, getYear } from '@/utils/DateHelpers'
import { INVOICE_STATUS_OPTIONS } from '@/constants/invoiceStatus'

const { start, end } = getWeekBounds()

const normaliseTransportItem = (item) => {
  const { item_detail, ...rest } = item
  const productCode = item_detail?.productcode

  return {
    ...item_detail,
    productCode,
    ...rest
  }
}

const normaliseRevenueDocket = (docket) => {
  const loadedItems = docket.loaded_items || []

  return {
    ...docket,
    loaded_items: loadedItems.map(normaliseTransportItem)
  }
}

const setFilterValue = (filters, payload) => {
  const filter = filters[payload.field]

  if (!filter) return

  if (filter.type === 'checkbox' && payload.value.length === 0) {
    filter.value.splice(0)
    return
  }

  if (filter.type === 'select') {
    filter.value = payload.value || ''
    return
  }

  filter.value = payload.value
}

const state = {
  revenueList: [],
  loadingRevenue: '',

  totalRevenue: 0,
  perPageRevenue: PER_PAGE_OPTIONS[0],
  pageNrRevenue: 0,

  sortbyRevenue: 'date|-,docket',

  filtersRevenue: {
    date_start: { type: 'date', label: 'Start Date', value: start },
    date_end: { type: 'date', label: 'End Date', value: end },
    docket: { type: 'text', label: 'Docket Number', value: '' },
    invoice: { type: 'text', label: 'Invoice Number', value: '' },
    invoice_status: {
      type: 'select',
      multiple: true,
      label: 'Invoice Status',
      selectLabel: 'text',
      value: '',
      name: 'value',
      options: INVOICE_STATUS_OPTIONS
    },
    delivery_by: {
      type: 'select',
      name: 'name',
      label: 'Delivered By',
      value: '',
      noReduce: true,
      optionsGetter: 'freightList',
      optionsFetchAction: 'fetchFreight',
      optionsSortBy: 'code',
      customOptions: (option) => {
        return `${option.code} - ${option.name}`
      },
      options: []
    },
    destination: {
      type: 'select',
      name: 'name',
      label: 'Customer',
      value: '',
      optionsGetter: 'destinationList',
      optionsFetchAction: 'fetchDestinations',
      optionsSortBy: 'name',
      options: []
    },
    week: { type: 'week', label: 'Week Nr', value: getISOWeekNumber() },
    year: { type: 'year', value: getYear(), hidden: true }
  }
}

const getters = {
  revenueList: (state) => state.revenueList,

  loadingRevenue: (state) => state.loadingRevenue,

  totalRevenue: (state) => state.totalRevenue,
  perPageRevenue: (state) => state.perPageRevenue,
  pageNumberRevenue: (state) => state.pageNrRevenue,
  sortbyRevenue: (state) => state.sortbyRevenue,

  filtersRevenue: (state) => state.filtersRevenue,

  offsetRevenue: (state) => {
    return state.perPageRevenue * state.pageNrRevenue
  }
}

const actions = {
  async fetchRevenue({ commit, dispatch, getters }) {
    commit('SET_LOADING_REVENUE', 'list')

    const { data, total, error } = await dispatch(
      'fetch',
      {
        endpoint: 'transport/',
        parameters: {
          limit: getters.perPageRevenue,
          offset: getters.offsetRevenue,
          sort_by: getters.sortbyRevenue
        },
        filters: getters.filtersRevenue
      },
      { root: true }
    )

    commit('SET_LOADING_REVENUE', '')

    if (error) {
      const messagePrefix = 'Error while loading Revenue Summary! '
      dispatch('showError', { error, messagePrefix })
      return
    }

    const revenueList = data.map(normaliseRevenueDocket)

    commit('SET_REVENUE_LIST', revenueList)
    commit('SET_TOTAL_REVENUE', total)
  },

  async updateRevenue({ rootGetters, commit, dispatch }, { payload }) {
    const URI = rootGetters.baseUrl + 'transport/' + payload.id
    commit('SET_LOADING_REVENUE', `update-${payload.id}`)

    try {
      const res = await axios.patch(URI, payload)
      const name = payload.company?.name ?? ''
      const message = `Revenue: ${name} information updated successfuly!`

      dispatch('showMessage', message)
      return res
    } catch (error) {
      const messagePrefix = 'Update Revenue Error! '
      dispatch('showError', { error, messagePrefix })
      return 'error'
    } finally {
      commit('SET_LOADING_REVENUE', '')
    }
  },

  async updateRevenuePrices({ rootGetters, commit, dispatch }, { payload }) {
    const URI = rootGetters.baseUrl + 'transport/update_prices/'
    const id = payload.id_list

    try {
      commit('SET_LOADING_REVENUE', `update-prices-${id}`)

      const config = { params: payload }
      const res = await axios.patch(URI, null, config)

      dispatch('showMessage', 'Docket prices recalculated successfully!')

      return res
    } catch (error) {
      const messagePrefix = 'Update Docket Prices Error'
      dispatch('showError', { error, messagePrefix })

      return 'error'
    } finally {
      commit('SET_LOADING_REVENUE', '')
    }
  },

  async generateRevenueInvoices({ rootGetters, commit, dispatch }, { idList }) {
    const URI = rootGetters.baseUrl + `transport/generate_invoice/${idList}`

    try {
      commit('SET_LOADING_REVENUE', 'generate-invoices')

      const res = await axios.post(URI)

      dispatch('showMessage', 'Exported dockets locked successfully.')

      return res
    } catch (error) {
      const messagePrefix = 'Lock Exported Dockets Error! '
      dispatch('showError', { error, messagePrefix })

      return 'error'
    } finally {
      commit('SET_LOADING_REVENUE', '')
    }
  },

  async unfreezeRevenueInvoice({ rootGetters, commit, dispatch }, { id }) {
    const URI = rootGetters.baseUrl + `transport/unfreeze_invoice/${id}`

    try {
      commit('SET_LOADING_REVENUE', `unfreeze-invoice-${id}`)

      const res = await axios.patch(URI)

      dispatch('showMessage', 'Invoice unlocked and reset to Draft.')

      return res
    } catch (error) {
      const messagePrefix = 'Unfreeze Invoice Error! '
      dispatch('showError', { error, messagePrefix })

      return 'error'
    } finally {
      commit('SET_LOADING_REVENUE', '')
    }
  },

  async clearFiltersRevenue({ commit }) {
    commit('SET_FILTER_REVENUE', { field: 'date_start', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'date_end', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'docket', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'invoice', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'invoice_status', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'destination', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'delivery_by', value: '' })
    commit('SET_FILTER_REVENUE', { field: 'week', value: null })
  }
}

const mutations = {
  SET_REVENUE_LIST: (state, payload) => {
    state.revenueList = payload
  },

  SET_TOTAL_REVENUE: (state, payload) => {
    state.totalRevenue = payload
  },

  SET_PAGE_NR_REVENUE: (state, payload) => {
    state.pageNrRevenue = payload
  },

  SET_PER_PAGE_REVENUE: (state, payload) => {
    state.perPageRevenue = payload
  },

  SET_SORT_BY_REVENUE: (state, payload) => {
    state.sortbyRevenue = payload
  },

  SET_LOADING_REVENUE: (state, payload) => {
    state.loadingRevenue = payload
  },

  SET_FILTER_REVENUE: (state, payload) => {
    setFilterValue(state.filtersRevenue, payload)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
